import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { 
  CreditCard, 
  Upload, 
  FileText, 
  TrendingUp, 
  DollarSign, 
  ArrowLeft,
  Download,
  Copy,
  Eye,
  EyeOff,
  PieChart,
  BarChart3
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
// Import jsPDF e jspdf-autotable
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function CreditCardAnalysis() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState('');
  const [processingStage, setProcessingStage] = useState('');
  const [validationStatus, setValidationStatus] = useState('idle'); // 'idle', 'error', 'success'
  const fileInputRef = useRef(null);

  // Categorias que não devem ser incluídas no cálculo total de despesas
  const excludedCategories = ['Pagamentos Recebidos', 'Reembolso'];

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
    
    if (!file) {
      setValidationStatus('error');
      return 'Nenhum arquivo selecionado.';
    }
    
    if (file.size > maxSize) {
      setValidationStatus('error');
      return 'Arquivo muito grande. Tamanho máximo: 10MB.';
    }
    
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.csv')) {
      setValidationStatus('error');
      return 'Formato inválido. Por favor, selecione um arquivo CSV.';
    }
    
    setValidationStatus('success');
    return null;
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    const error = validateFile(selectedFile);
    
    if (error) {
      setValidationError(error);
      setTimeout(() => {
        setValidationError('');
        setValidationStatus('idle');
      }, 5000);
      return;
    }
    
    setValidationError('');
    setFile(selectedFile);
    processFile(selectedFile);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const droppedFile = event.dataTransfer.files[0];
    const error = validateFile(droppedFile);
    
    if (error) {
      setValidationError(error);
      setTimeout(() => {
        setValidationError('');
        setValidationStatus('idle');
      }, 5000);
      return;
    }
    
    setValidationError('');
    setFile(droppedFile);
    processFile(droppedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };
  
  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const processFile = async (file) => {
    setLoading(true);
    setUploadProgress(0);
    setProcessingStage('Iniciando processamento...');
    
    try {
      // Simulate progressive loading stages
      setProcessingStage('Lendo arquivo...');
      setUploadProgress(25);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const text = await file.text();
      
      setProcessingStage('Analisando dados...');
      setUploadProgress(50);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setProcessingStage('Processando categorias...');
      setUploadProgress(75);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const parsedData = parseCSV(text);
      
      setProcessingStage('Finalizando...');
      setUploadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setData(parsedData);
      setActiveTab('overview');
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      setValidationError('Erro ao processar o arquivo CSV. Verifique o formato.');
      setTimeout(() => setValidationError(''), 5000);
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setProcessingStage('');
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const categories = {};
    let currentCategory = null;
    
    lines.forEach(line => {
      if (line.startsWith('##')) {
        currentCategory = line.replace('##', '').trim();
        categories[currentCategory] = [];
      } else if (currentCategory && line.includes(',')) {
        const [date, title, amount] = line.split(',').map(item => item.trim());
        if (date && title && amount) {
          categories[currentCategory].push({
            date,
            title: title.replace(/"/g, ''),
            amount: parseFloat(amount)
          });
        }
      }
    });

    // Calcular totais por categoria
    const categoryTotals = {};
    let totalExpenses = 0;
    let totalIncome = 0;

    Object.keys(categories).forEach(category => {
      const total = categories[category].reduce((sum, item) => sum + item.amount, 0);
      categoryTotals[category] = total;
      
      if (excludedCategories.includes(category)) {
        totalIncome += Math.abs(total);
      } else {
        totalExpenses += Math.abs(total);
      }
    });

    return {
      categories,
      categoryTotals,
      totalExpenses,
      totalIncome,
      netBalance: totalIncome - totalExpenses
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Math.abs(value));
  };

  const getCategoryColor = (category, index) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-indigo-500', 'bg-red-500', 'bg-yellow-500',
      'bg-teal-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-violet-500'
    ];
    return colors[index % colors.length];
  };

  // Função para ordenar categorias: despesas por percentual decrescente, depois receitas
  const getSortedCategories = (data) => {
    if (!data || !data.categoryTotals) return [];
    
    const categories = Object.keys(data.categoryTotals);
    
    // Separar categorias de despesa e receita
    const expenseCategories = categories.filter(category => !excludedCategories.includes(category));
    const incomeCategories = categories.filter(category => excludedCategories.includes(category));
    
    // Ordenar categorias de despesa por percentual decrescente
    const sortedExpenseCategories = expenseCategories.sort((a, b) => {
      const percentA = Math.abs(data.categoryTotals[a]) / data.totalExpenses * 100;
      const percentB = Math.abs(data.categoryTotals[b]) / data.totalExpenses * 100;
      return percentB - percentA; // Ordem decrescente
    });
    
    // Ordenar categorias de receita por percentual decrescente
    const sortedIncomeCategories = incomeCategories.sort((a, b) => {
      const percentA = Math.abs(data.categoryTotals[a]) / data.totalIncome * 100;
      const percentB = Math.abs(data.categoryTotals[b]) / data.totalIncome * 100;
      return percentB - percentA; // Ordem decrescente
    });
    
    // Retornar: despesas ordenadas + receitas ordenadas
    return [...sortedExpenseCategories, ...sortedIncomeCategories];
  };

  const toggleCategoryDetails = (category) => {
    setShowDetails(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Dados copiados para a área de transferência!');
  };

  const downloadJSON = () => {
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'credit-card-analysis.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    let yPosition = margin;

    // Título do relatório
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text('Relatório de Análise de Cartão de Crédito', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;

    // Data de geração
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    const currentDate = new Date().toLocaleDateString('pt-BR');
    doc.text(`Gerado em: ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;

    // Resumo financeiro
    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);
    doc.text('Resumo Financeiro', margin, yPosition);
    yPosition += 10;

    const summaryData = [
      ['Total Recebido', formatCurrency(data.totalIncome)],
      ['Total Despesas', formatCurrency(data.totalExpenses)],
      ['Saldo Líquido', formatCurrency(data.netBalance)]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [['Descrição', 'Valor']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [219, 39, 119], textColor: 255 },
      styles: { fontSize: 12, cellPadding: 5 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 60, halign: 'right' }
      }
    });

    yPosition = doc.lastAutoTable.finalY + 20;

    // Detalhes por categoria
    doc.setFontSize(16);
    doc.text('Detalhes por Categoria', margin, yPosition);
    yPosition += 15;

    Object.keys(data.categories).forEach((category, index) => {
      // Verificar se precisa de nova página
      if (yPosition > doc.internal.pageSize.height - 60) {
        doc.addPage();
        yPosition = margin;
      }

      // Título da categoria
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      const categoryTotal = data.categoryTotals[category];
      const isIncome = excludedCategories.includes(category);
      const categoryTitle = `${category} - ${isIncome ? '+' : '-'}${formatCurrency(Math.abs(categoryTotal))}`;
      doc.text(categoryTitle, margin, yPosition);
      yPosition += 10;

      // Preparar dados da tabela para esta categoria
      const categoryTableData = data.categories[category].map(item => [
        item.date,
        item.title,
        `${item.amount < 0 ? '+' : '-'}${formatCurrency(Math.abs(item.amount))}`
      ]);

      // Adicionar linha de total
      categoryTableData.push([
        '',
        'TOTAL DA CATEGORIA',
        `${isIncome ? '+' : '-'}${formatCurrency(Math.abs(categoryTotal))}`
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Data', 'Descrição', 'Valor']],
        body: categoryTableData,
        theme: 'striped',
        headStyles: { 
          fillColor: isIncome ? [34, 197, 94] : [239, 68, 68], 
          textColor: 255 
        },
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 100 },
          2: { cellWidth: 40, halign: 'right' }
        },
        didParseCell: function(data) {
          // Destacar a linha de total
          if (data.row.index === categoryTableData.length - 1) {
            data.cell.styles.fillColor = [245, 245, 245];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      yPosition = doc.lastAutoTable.finalY + 15;
    });

    // Rodapé
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Página ${i} de ${totalPages}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    // Salvar o PDF
    const fileName = `analise-cartao-credito-${currentDate.replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="relative mb-8">
          <Card className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <Link to={createPageUrl("Operations")}>
                  <Button variant="outline" size="sm" isGlass className="flex items-center space-x-2">
                    <ArrowLeft className="w-4 h-4" />
                    <span>Voltar</span>
                  </Button>
                </Link>
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Credit Card Analysis</h1>
                    <p className="text-gray-600">Análise detalhada de despesas do cartão de crédito</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {!data ? (
          /* Upload Area */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8"
          >
            {/* Enhanced Upload Zone with Glass Morphism */}
            <div className="relative group">
              {/* Static gradient background */}
              <div 
                className={`absolute inset-0 ${
                  isDragOver 
                    ? 'bg-purple-500' 
                    : validationStatus === 'error'
                      ? 'bg-red-500'
                      : validationStatus === 'success'
                        ? 'bg-green-500'
                        : 'bg-purple-500'
                } rounded-2xl blur-xl opacity-30 ${
                  isDragOver 
                    ? 'opacity-70' 
                    : 'group-hover:opacity-50'
                } transition-all duration-500`}
              ></div>
              
              {/* Enhanced Glass morphism card with animated gradient border */}
              <div className={`relative h-full rounded-2xl p-[2px] overflow-hidden transition-all duration-300 ${
                isDragOver ? 'scale-[1.02]' : ''
              }`}>
                {/* Static border gradient */}
                <div className={`absolute inset-0 rounded-2xl ${
                  isDragOver 
                    ? 'bg-purple-500' 
                    : validationStatus === 'error'
                      ? 'bg-red-500'
                      : validationStatus === 'success'
                        ? 'bg-green-500'
                        : 'bg-purple-500'
                } opacity-90`}></div>
                
                <Card 
                  isGlass 
                  className={`relative h-full backdrop-blur-xl ${
                    isDragOver 
                      ? 'bg-white/40 shadow-glass-hover' 
                      : 'bg-white/30'
                  } rounded-2xl overflow-hidden transition-all duration-300 border-0`}
                >
                  <CardContent className="p-6 md:p-8">
                    <div
                      className={`text-center space-y-6 h-full flex flex-col justify-center items-center ${
                        isDragOver ? 'py-8' : ''
                      }`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDragEnter={handleDragEnter}
                    >
                      {/* Enhanced icon with improved animation */}
                      <motion.div 
                        className="flex justify-center"
                        animate={{
                          scale: isDragOver ? 1.15 : 1,
                          y: isDragOver ? -15 : 0,
                          rotate: isDragOver ? 5 : 0
                        }}
                        transition={{ 
                          type: "spring", 
                          stiffness: 300, 
                          damping: 15 
                        }}
                      >
                        <div className={`p-6 ${
                          validationStatus === 'error'
                            ? 'bg-gradient-to-br from-rose-500 to-red-500'
                            : validationStatus === 'success'
                              ? 'bg-gradient-to-br from-emerald-500 to-green-500'
                              : 'bg-gradient-to-br from-primary-500 to-secondary-500'
                          } rounded-full shadow-xl transform group-hover:scale-105 transition-all duration-300`}>
                          <Upload className={`w-12 h-12 text-white ${isDragOver ? 'animate-pulse' : ''}`} />
                        </div>
                      </motion.div>
                      
                      {/* Enhanced content with improved conditional rendering */}
                      <div className="space-y-4">
                        <AnimatePresence mode="wait">
                          {loading ? (
                            <motion.div 
                              key="loading"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="space-y-6"
                            >
                              <h3 className="text-xl md:text-2xl font-semibold text-gray-900">
                                {processingStage}
                              </h3>
                              
                              {/* Enhanced progress bar with animation */}
                              <div className="w-full max-w-md mx-auto">
                                <div className="h-3 w-full bg-gray-200/50 backdrop-blur-sm rounded-full overflow-hidden shadow-inner">
                                  <motion.div 
                                    className="h-full bg-purple-500"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${uploadProgress}%` }}
                                    transition={{ duration: 0.5 }}
                                  />
                                </div>
                                <div className="flex justify-between mt-2 text-sm text-gray-700">
                                  <span>Processando...</span>
                                  <span className="font-medium">{uploadProgress}%</span>
                                </div>
                              </div>
                              
                              {/* Enhanced skeleton loading UI with glass morphism */}
                              <div className="space-y-4 max-w-md mx-auto w-full">
                                <div className="flex items-center space-x-4">
                                  <div className="h-12 w-12 rounded-full bg-white/40 backdrop-blur-sm animate-pulse"></div>
                                  <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-white/40 backdrop-blur-sm rounded animate-pulse"></div>
                                    <div className="h-3 bg-white/30 backdrop-blur-sm rounded animate-pulse w-3/4"></div>
                                  </div>
                                </div>
                                <div className="h-20 bg-white/30 backdrop-blur-sm rounded-lg animate-pulse"></div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div className="h-8 bg-white/30 backdrop-blur-sm rounded animate-pulse"></div>
                                  <div className="h-8 bg-white/30 backdrop-blur-sm rounded animate-pulse"></div>
                                  <div className="h-8 bg-white/30 backdrop-blur-sm rounded animate-pulse"></div>
                                </div>
                              </div>
                            </motion.div>
                          ) : isDragOver ? (
                            <motion.div
                              key="dragover"
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              className="space-y-4"
                            >
                              <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-3">
                                Solte o arquivo aqui
                              </h3>
                              <div className="p-4 bg-white/40 backdrop-blur-md rounded-xl border border-white/50 shadow-sm">
                                <p className="text-gray-700 max-w-md mx-auto">
                                  Solte seu arquivo CSV para iniciar o processamento
                                </p>
                              </div>
                              
                              {/* Visual indicator for drag state */}
                              <div className="w-16 h-1 bg-purple-500 rounded-full mx-auto opacity-70" />
                            </motion.div>
                          ) : (
                            <motion.div
                              key="default"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="space-y-4"
                            >
                              <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-3">
                                Upload do Arquivo CSV
                              </h3>
                              <div className="p-4 bg-white/40 backdrop-blur-md rounded-xl border border-white/50 shadow-sm">
                                <p className="text-gray-700 max-w-md mx-auto">
                                  Arraste e solte seu arquivo CSV aqui ou clique para selecionar
                                </p>
                              </div>
                              
                              {/* Enhanced validation error message with animation */}
                              <AnimatePresence>
                                {validationError && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="mb-4 p-4 bg-rose-50/80 backdrop-blur-sm border border-rose-200 rounded-xl text-rose-700 text-sm max-w-md mx-auto shadow-sm"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="flex-shrink-0 p-1.5 bg-rose-100 rounded-full">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                      <div>
                                        <p className="font-medium">Erro de validação</p>
                                        <p>{validationError}</p>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                              
                              {/* Enhanced button with animation */}
                              <motion.div
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                              >
                                <Button
                                  onClick={() => fileInputRef.current?.click()}
                                  variant="gradient"
                                  size="lg"
                                  className="shadow-xl px-8 py-6 h-auto"
                                  disabled={loading}
                                >
                                  <span className="flex items-center space-x-2">
                                    <Upload className="w-5 h-5 mr-2" />
                                    <span>Selecionar Arquivo</span>
                                  </span>
                                </Button>
                              </motion.div>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileSelect}
                                className="hidden"
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Enhanced File Format Guide with Glass Morphism */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-secondary-400 via-primary-500 to-secondary-400 rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
              <Card isGlass className="relative backdrop-blur-xl bg-white/30 border border-white/30 rounded-2xl h-full shadow-glass group-hover:shadow-glass-hover transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-2.5 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg shadow-md">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <span>Formato do Arquivo</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-white/50 backdrop-blur-md p-5 rounded-xl shadow-sm border border-white/50 transition-all duration-300 hover:bg-white/60">
                    <h4 className="font-semibold mb-3 text-gray-800">Estrutura esperada:</h4>
                    <div className="relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-50/50 to-white/50 backdrop-blur-sm rounded-lg"></div>
                      <code className="relative text-sm text-gray-700 block bg-gray-50/80 p-4 rounded-lg border border-gray-100 shadow-sm">
                        <span className="text-primary-600">date</span>,<span className="text-secondary-600">title</span>,<span className="text-emerald-600">amount</span><br/>
                        <span className="text-gray-500">## Categoria</span><br/>
                        <span className="text-primary-600">2025-06-25</span>,<span className="text-secondary-600">Descrição</span>,<span className="text-emerald-600">-100.00</span>
                      </code>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800">Categorias suportadas:</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      {[
                        { name: 'Supermercado', color: 'blue' },
                        { name: 'Restaurantes', color: 'green' },
                        { name: 'Marketplaces', color: 'purple' },
                        { name: 'Assinaturas', color: 'orange' },
                        { name: 'Farmácias', color: 'pink' },
                        { name: 'Gasolina', color: 'indigo' }
                      ].map((category, index) => (
                        <motion.div
                          key={index}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`bg-${category.color}-100/80 backdrop-blur-sm px-3 py-2 rounded-full text-${category.color}-700 text-center border border-${category.color}-200/50 shadow-sm transition-all duration-300 hover:shadow-md hover:bg-${category.color}-100`}
                        >
                          {category.name}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Added file validation tips */}
                  <div className="bg-white/40 backdrop-blur-sm p-4 rounded-xl border border-white/50 shadow-sm">
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Dicas de validação:
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-1.5 ml-5 list-disc">
                      <li>Tamanho máximo: 10MB</li>
                      <li>Formato: CSV (valores separados por vírgula)</li>
                      <li>Codificação: UTF-8 recomendada</li>
                      <li>Datas no formato: AAAA-MM-DD</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        ) : (
          /* Analysis Results */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Summary Cards with 3D Effects */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {/* Income Card with 3D Effects */}
              <motion.div 
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {/* Enhanced gradient background with animation */}
                <div className="absolute inset-0 bg-green-500 rounded-2xl blur opacity-70 group-hover:opacity-90 transition duration-500"></div>
                
                {/* Card with enhanced 3D transform effects */}
                <Card isGlass className="relative h-full border border-white/30 backdrop-blur-md bg-white/10 rounded-2xl overflow-hidden shadow-glass
                ">
                  <CardContent className="p-6 md:p-8 relative z-10">
                    <div className="flex flex-col space-y-6">
                      {/* Icon with enhanced 3D animation */}
                      <motion.div 
                        className="p-4 bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl shadow-lg w-fit transform-gpu"
                        style={{
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                          transform: 'perspective(1000px) rotateX(10deg) rotateY(5deg)'
                        }}
                      >
                        <TrendingUp className="w-8 h-8 text-white drop-shadow-md" />
                      </motion.div>
                      
                      <div className="space-y-2">
                        <p className="text-white/90 font-medium text-xs sm:text-sm md:text-base tracking-wide uppercase">Total Recebido</p>
                        
                        {/* Animated counter component with enhanced 3D effect */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ 
                            opacity: 1, 
                            scale: 1,
                            rotateX: [0, 10, 0],
                            transition: { duration: 1.2 }
                          }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                          className="relative"
                          style={{
                            textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                            transform: 'perspective(1000px)'
                          }}
                        >
                          <motion.p 
                            className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                              opacity: 1, 
                              y: 0,
                              transition: { 
                                type: "spring",
                                stiffness: 100,
                                damping: 10,
                                delay: 0.4
                              }
                            }}
                          >
                            {formatCurrency(data.totalIncome)}
                          </motion.p>
                          
                          {/* Enhanced highlight effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-white/40 to-transparent opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500"></div>
                        </motion.div>
                        
                        {/* Added transaction count indicator */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 }}
                          className="mt-2"
                        >
                          <p className="text-white/70 text-xs sm:text-sm">
                            {Object.keys(data.categories)
                              .filter(cat => excludedCategories.includes(cat))
                              .reduce((count, cat) => count + data.categories[cat].length, 0)} transações
                          </p>
                        </motion.div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Expenses Card with 3D Effects */}
              <motion.div 
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Enhanced gradient background with animation */}
                <div className="absolute inset-0 bg-red-500 rounded-2xl blur opacity-70 group-hover:opacity-90 transition duration-500"></div>
                
                {/* Card with enhanced 3D transform effects */}
                <Card isGlass className="relative h-full border border-white/30 backdrop-blur-md bg-white/10 rounded-2xl overflow-hidden shadow-glass
                ">
                  <CardContent className="p-6 md:p-8 relative z-10">
                    <div className="flex flex-col space-y-6">
                      {/* Icon with enhanced 3D animation */}
                      <motion.div 
                        className="p-4 bg-gradient-to-br from-rose-300 to-red-400 rounded-xl shadow-lg w-fit transform-gpu"
                        style={{
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                          transform: 'perspective(1000px) rotateX(10deg) rotateY(-5deg)'
                        }}
                      >
                        <DollarSign className="w-8 h-8 text-white drop-shadow-md" />
                      </motion.div>
                      
                      <div className="space-y-2">
                        <p className="text-white/90 font-medium text-xs sm:text-sm md:text-base tracking-wide uppercase">Total Despesas</p>
                        
                        {/* Animated counter component with enhanced 3D effect */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ 
                            opacity: 1, 
                            scale: 1,
                            rotateX: [0, -10, 0],
                            transition: { duration: 1.2 }
                          }}
                          transition={{ duration: 0.5, delay: 0.4 }}
                          className="relative"
                          style={{
                            textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                            transform: 'perspective(1000px)'
                          }}
                        >
                          <motion.p 
                            className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                              opacity: 1, 
                              y: 0,
                              transition: { 
                                type: "spring",
                                stiffness: 100,
                                damping: 10,
                                delay: 0.5
                              }
                            }}
                          >
                            {formatCurrency(data.totalExpenses)}
                          </motion.p>
                          
                          {/* Enhanced highlight effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-white/40 to-transparent opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500"></div>
                        </motion.div>
                        
                        {/* Added category count indicator */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.7 }}
                          className="mt-2"
                        >
                          <p className="text-white/70 text-xs sm:text-sm">
                            {Object.keys(data.categories)
                              .filter(cat => !excludedCategories.includes(cat))
                              .length} categorias
                          </p>
                        </motion.div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Balance Card with 3D Effects */}
              <motion.div 
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {/* Enhanced gradient background with animation */}
                <div className={`absolute inset-0 ${
                  data.netBalance >= 0 
                    ? 'bg-blue-500' 
                    : 'bg-orange-500'
                } rounded-2xl blur opacity-70 group-hover:opacity-90 transition duration-500`}></div>
                
                {/* Card with enhanced 3D transform effects */}
                <Card isGlass className="relative h-full border border-white/30 backdrop-blur-md bg-white/10 rounded-2xl overflow-hidden shadow-glass
                ">
                  <CardContent className="p-6 md:p-8 relative z-10">
                    <div className="flex flex-col space-y-6">
                      {/* Icon with enhanced 3D animation */}
                      <motion.div 
                        className={`p-4 bg-gradient-to-br ${
                          data.netBalance >= 0 
                            ? 'from-blue-300 to-cyan-400' 
                            : 'from-amber-300 to-orange-400'
                        } rounded-xl shadow-lg w-fit transform-gpu`}
                        style={{
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                          transform: 'perspective(1000px) rotateX(10deg) rotateY(-3deg)'
                        }}
                      >
                        <BarChart3 className="w-8 h-8 text-white drop-shadow-md" />
                      </motion.div>
                      
                      <div className="space-y-2">
                        <p className="text-white/90 font-medium text-xs sm:text-sm md:text-base tracking-wide uppercase">Saldo Líquido</p>
                        
                        {/* Animated counter component with enhanced 3D effect */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ 
                            opacity: 1, 
                            scale: 1,
                            rotateX: [0, -10, 0, 5, 0],
                            transition: { duration: 1.5 }
                          }}
                          transition={{ duration: 0.5, delay: 0.5 }}
                          className="relative"
                          style={{
                            textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                            transform: 'perspective(1000px)'
                          }}
                        >
                          <motion.p 
                            className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                              opacity: 1, 
                              y: 0,
                              transition: { 
                                type: "spring",
                                stiffness: 100,
                                damping: 10,
                                delay: 0.6
                              }
                            }}
                          >
                            {data.netBalance >= 0 ? '+' : '-'}{formatCurrency(Math.abs(data.netBalance))}
                          </motion.p>
                          
                          {/* Enhanced highlight effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-white/40 to-transparent opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500"></div>
                        </motion.div>
                        
                        {/* Added balance indicator */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.8 }}
                          className="mt-2"
                        >
                          <p className="text-white/70 text-xs sm:text-sm">
                            {data.netBalance >= 0 ? 'Saldo positivo' : 'Saldo negativo'} • {
                              Math.round(Math.abs(data.netBalance) / data.totalExpenses * 100)
                            }% da despesa
                          </p>
                        </motion.div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Enhanced Modern Tabs System with Advanced Micro-interactions */}
            <motion.div 
              className="relative mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Enhanced background glow effect - static purple */}
              <div className="absolute inset-0 bg-purple-500/30 rounded-xl blur-lg opacity-70"></div>
              
              {/* Enhanced Glass morphism container with improved styling */}
              <motion.div 
                className="relative bg-white/70 backdrop-blur-xl p-2 rounded-xl shadow-glass border border-white/50 transition-all duration-300"
              >
                {/* Tab buttons with modern pill-style design */}
                <div className="flex w-full relative gap-2" role="tablist">
                  {/* Overview Tab - Enhanced with advanced micro-interactions */}
                  <motion.button
                    onClick={() => setActiveTab('overview')}
                    className={`
                      relative z-10 py-3 px-6 rounded-lg font-medium transition-all duration-300
                      flex items-center justify-center flex-1 md:flex-none text-center
                      ${activeTab === 'overview' 
                        ? 'text-white shadow-md' 
                        : 'text-gray-700'
                      }
                      focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:ring-offset-1
                      overflow-hidden
                    `}
                    aria-selected={activeTab === 'overview'}
                    role="tab"
                  >
                    {/* Ripple effect background */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-primary-400/20 to-secondary-400/20 rounded-lg"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ 
                        scale: activeTab === 'overview' ? 1 : 0,
                        opacity: activeTab === 'overview' ? 1 : 0
                      }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    <div className="flex items-center space-x-2 relative z-10">
                      <motion.div 
                        className={`
                          p-1.5 rounded-full transition-all duration-300
                          ${activeTab === 'overview' 
                            ? 'bg-white/20' 
                            : 'bg-primary-100'
                          }
                        `}
                      >
                        <PieChart className={`w-4 h-4 transition-all duration-300 ${activeTab === 'overview' ? 'text-white' : 'text-primary-500'}`} />
                      </motion.div>
                      <motion.span
                        className="text-sm md:text-base font-medium"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                      >
                        Visão Geral
                      </motion.span>
                    </div>
                  </motion.button>
                  
                  {/* Details Tab - Enhanced with advanced micro-interactions */}
                  <motion.button
                    onClick={() => setActiveTab('details')}
                    className={`
                      relative z-10 py-3 px-6 rounded-lg font-medium transition-all duration-300
                      flex items-center justify-center flex-1 md:flex-none text-center
                      ${activeTab === 'details' 
                        ? 'text-white shadow-md' 
                        : 'text-gray-700'
                      }
                      focus:outline-none focus:ring-2 focus:ring-secondary-400/50 focus:ring-offset-1
                      overflow-hidden
                    `}
                    aria-selected={activeTab === 'details'}
                    role="tab"
                  >
                    {/* Ripple effect background */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-secondary-400/20 to-primary-400/20 rounded-lg"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ 
                        scale: activeTab === 'details' ? 1 : 0,
                        opacity: activeTab === 'details' ? 1 : 0
                      }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    <div className="flex items-center space-x-2 relative z-10">
                      <motion.div 
                        className={`
                          p-1.5 rounded-full transition-all duration-300
                          ${activeTab === 'details' 
                            ? 'bg-white/20' 
                            : 'bg-secondary-100'
                          }
                        `}
                      >
                        <BarChart3 className={`w-4 h-4 transition-all duration-300 ${activeTab === 'details' ? 'text-white' : 'text-secondary-500'}`} />
                      </motion.div>
                      <motion.span
                        className="text-sm md:text-base font-medium"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                      >
                        Detalhes por Categoria
                      </motion.span>
                    </div>
                  </motion.button>
                  
                  {/* Enhanced animated active tab indicator with improved transitions and 3D effects */}
                  <motion.div 
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary-500 to-secondary-600 rounded-lg shadow-lg"
                    initial={false}
                    animate={{
                      x: activeTab === 'overview' ? 0 : 'calc(100% + 8px)',
                      width: activeTab === 'overview' ? 'calc(50% - 4px)' : 'calc(50% - 4px)',
                      transition: {
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                        duration: 0.6
                      }
                    }}
                    style={{
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      background: activeTab === 'overview' 
                        ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)'
                        : 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)'
                    }}
                    aria-hidden="true"
                  >
                    {/* Inner glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-lg" />
                    

                  </motion.div>
                </div>
              </motion.div>
              
              {/* Tab indicator dots - Static */}
              <div className="flex justify-center mt-3 space-x-2">
                <div 
                  className={`h-1.5 rounded-full ${activeTab === 'overview' ? 'bg-purple-500 w-6' : 'bg-gray-300 w-1.5'}`}
                />
                <div 
                  className={`h-1.5 rounded-full ${activeTab === 'details' ? 'bg-purple-500 w-6' : 'bg-gray-300 w-1.5'}`}
                />
              </div>
            </motion.div>

            <AnimatePresence mode="wait">
              {activeTab === 'overview' && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
                >
                  {getSortedCategories(data).map((category, index) => (
                    <motion.div 
                      key={category} 
                      className="relative group"
                      initial={{ 
                        opacity: 0, 
                        y: 30, 
                        scale: 0.8,
                        rotateX: -15,
                        rotateY: 10
                      }}
                      animate={{ 
                        opacity: 1, 
                        y: 0, 
                        scale: 1,
                        rotateX: 0,
                        rotateY: 0
                      }}
                      transition={{ 
                        duration: 0.8, 
                        delay: index * 0.15,
                        type: "spring",
                        stiffness: 120,
                        damping: 12,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}

                      whileTap={{ 
                        scale: 0.98,
                        y: -2,
                        transition: { duration: 0.1 }
                      }}
                      style={{
                        transformStyle: 'preserve-3d',
                        perspective: '1200px'
                      }}
                    >
                      {/* Enhanced glow effect with category-specific colors */}
                      <motion.div 
                        className={`absolute inset-0 ${getCategoryColor(category, index).replace('bg-', 'bg-').replace('500', '300')} rounded-xl blur-md opacity-20`}
                        animate={{
                          opacity: [0.2, 0.6, 0.2],
                          scale: [1, 1.08, 1],
                          rotate: [0, 1, 0]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: index * 0.3
                        }}
                        whileHover={{
                          opacity: 0.8,
                          scale: 1.1,
                          transition: { duration: 0.3 }
                        }}
                      />
                      
                      {/* Enhanced Card */}
                      <motion.div
                        className="relative h-full"
                        style={{
                          transformStyle: 'preserve-3d',
                          perspective: '1200px'
                        }}
                      >
                        <Card 
                          className="relative h-full bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                          style={{
                            transform: 'translateZ(0)',
                            backfaceVisibility: 'hidden'
                          }}
                        >
                          <CardHeader className="pb-3 relative">
                            {/* Animated background pattern */}
                            <div className="absolute inset-0 opacity-5">
                              <div className="w-full h-full bg-gradient-to-br from-current to-transparent" />
                            </div>
                            
                            <CardTitle className="flex items-center justify-between text-lg relative z-10">
                              <motion.span 
                                className="flex items-center space-x-2"
                              >
                                {/* Enhanced category icon with pulse animation */}
                                <div className={`w-4 h-4 rounded-full ${getCategoryColor(category, index)} shadow-sm`}></div>
                                
                                <motion.span 
                                  className="font-medium"
                                >
                                  {category}
                                </motion.span>
                              </motion.span>
                              
                              <span 
                                className={`text-sm font-bold px-3 py-1.5 rounded-full shadow-sm cursor-pointer ${
                                  excludedCategories.includes(category) 
                                    ? 'bg-green-100 text-green-700 border border-green-200' 
                                    : 'bg-red-100 text-red-700 border border-red-200'
                                }`}
                              >
                                {excludedCategories.includes(category) ? '+' : '-'}{formatCurrency(Math.abs(data.categoryTotals[category]))}
                              </span>
                            </CardTitle>
                          </CardHeader>
                          
                          <CardContent className="relative">
                            <div className="space-y-4">
                              {/* Enhanced transaction count with icon */}
                              <motion.div 
                                className="flex justify-between items-center"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 + index * 0.1 }}
                              >
                                <span className="text-sm text-gray-600 flex items-center space-x-1">
                                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                                  <span>Transações:</span>
                                </span>
                                <motion.span 
                                  className="font-medium bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-1 rounded-full text-blue-700 border border-blue-200"
                                >
                                  {data.categories[category].length}
                                </motion.span>
                              </motion.div>
                              
                              {/* Enhanced average with icon */}
                              <motion.div 
                                className="flex justify-between items-center"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 + index * 0.1 }}
                              >
                                <span className="text-sm text-gray-600 flex items-center space-x-1">
                                  <div className="w-2 h-2 bg-purple-400 rounded-full" />
                                  <span>Média por transação:</span>
                                </span>
                                <motion.span 
                                  className="font-medium bg-gradient-to-r from-purple-50 to-pink-50 px-3 py-1 rounded-full text-purple-700 border border-purple-200"
                                >
                                  {formatCurrency(Math.abs(data.categoryTotals[category]) / data.categories[category].length)}
                                </motion.span>
                              </motion.div>
                              
                              {/* Enhanced progress bar with animations */}
                              <motion.div 
                                className="pt-2"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 + index * 0.1 }}
                              >
                                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                                  {/* Background shimmer effect */}
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                                  
                                  {/* Animated progress bar */}
                                  <motion.div 
                                    className={`${getCategoryColor(category, index)} h-2 rounded-full relative overflow-hidden shadow-sm`}
                                    initial={{ width: 0, opacity: 0 }}
                                    animate={{ 
                                      width: `${Math.min(100, Math.abs(data.categoryTotals[category]) / (excludedCategories.includes(category) ? data.totalIncome : data.totalExpenses) * 100)}%`,
                                      opacity: 1
                                    }}
                                    transition={{ 
                                      duration: 2, 
                                      delay: 0.8 + index * 0.15,
                                      type: "spring",
                                      stiffness: 80,
                                      damping: 15
                                    }}
                                  >
                                    {/* Inner glow effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
                                    

                                  </motion.div>
                                </div>
                                
                                {/* Enhanced percentage display */}
                                <motion.div 
                                  className="flex justify-between mt-2"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 1 + index * 0.1 }}
                                >
                                  <span className="text-xs text-gray-500 flex items-center space-x-1">
                                    <div className="w-1 h-1 bg-gray-400 rounded-full" />
                                    <span>% do total</span>
                                  </span>
                                  <motion.span 
                                    className="text-xs font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full"
                                  >
                                    {(Math.abs(data.categoryTotals[category]) / (excludedCategories.includes(category) ? data.totalIncome : data.totalExpenses) * 100).toFixed(1)}%
                                  </motion.span>
                                </motion.div>
                              </motion.div>
                            </div>
                            

                          </CardContent>
                        </Card>
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>
              )}

              {activeTab === 'details' && (
                <motion.div
                  key="details"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  {getSortedCategories(data).map((category, index) => (
                    <div key={category} className="relative">
                      <Card 
                        className="relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <CardHeader 
                          className="cursor-pointer transition-colors"
                          onClick={() => toggleCategoryDetails(category)}
                        >
                          <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center space-x-3">
                              <div className={`w-5 h-5 rounded-full ${getCategoryColor(category, index)} shadow-sm flex items-center justify-center`}>
                                {showDetails[category] ? 
                                  <EyeOff className="w-3 h-3 text-white" /> : 
                                  <Eye className="w-3 h-3 text-white" />
                                }
                              </div>
                              <span className="font-medium">{category}</span>
                              <span className="text-sm bg-gray-100 px-2 py-0.5 rounded-full text-gray-700">
                                {data.categories[category].length} transações
                              </span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className={`font-bold px-3 py-1 rounded-full ${
                                excludedCategories.includes(category) 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {excludedCategories.includes(category) ? '+' : '-'}{formatCurrency(Math.abs(data.categoryTotals[category]))}
                              </span>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        
                        <AnimatePresence>
                          {showDetails[category] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <CardContent className="pt-0">
                                <div className="space-y-2 max-h-64 overflow-y-auto pr-1 scrollbar-thin">
                                  {[...data.categories[category]]
                                    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
                                    .map((item, itemIndex) => (
                                    <div 
                                      key={itemIndex} 
                                      className="flex justify-between items-center py-2 px-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-lg shadow-sm hover:shadow transition-shadow"
                                    >
                                      <div>
                                        <p className="font-medium text-sm">{item.title}</p>
                                        <p className="text-xs text-gray-500">{item.date}</p>
                                      </div>
                                      <span className={`font-bold px-2 py-1 rounded-full text-sm ${
                                        item.amount < 0 
                                          ? 'bg-green-50 text-green-700' 
                                          : 'bg-red-50 text-red-700'
                                      }`}>
                                        {item.amount < 0 ? '+' : '-'}{formatCurrency(Math.abs(item.amount))}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Card>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="relative">
              <Card className="relative bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => copyToClipboard(JSON.stringify(data, null, 2))}
                    variant="outline"
                    isGlass
                    className="flex items-center space-x-2"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copiar JSON</span>
                  </Button>
                  <Button
                    onClick={downloadPDF}
                    variant="gradient"
                    className="flex items-center space-x-2 shadow-md"
                  >
                    <Download className="w-4 h-4" />
                    <span>Baixar Relatório PDF</span>
                  </Button>
                  <Button
                    onClick={downloadJSON}
                    variant="outline"
                    isGlass
                    className="flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Baixar JSON</span>
                  </Button>
                  <Button
                    onClick={() => {
                      setData(null);
                      setFile(null);
                      setShowDetails({});
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    variant="secondary"
                    className="flex items-center space-x-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Nova Análise</span>
                  </Button>
                </div>
              </Card>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
